import React, { useState, useEffect } from 'react';
import { authenticatedFetch } from '../utils/auth';
import './Profile.css';

const Profile = ({ user, onClose, onUpdate }) => {
  const [activeTab, setActiveTab] = useState('paper'); // paper: 모의투자, real: 실전투자
  const [isPaperTrading, setIsPaperTrading] = useState(true); // 기본값: 모의투자
  const [formData, setFormData] = useState({
    nickname: '',
    name: '',
    email: '',
    // 모의투자
    paper_app_key: '',
    paper_app_secret: '',
    paper_account_no: '',
    paper_hts_id: '',
    paper_account_stock: '',
    paper_account_future: '',
    paper_prod: '01',
    // 실전투자
    real_app_key: '',
    real_app_secret: '',
    real_account_no: '',
    real_hts_id: '',
    real_account_stock: '',
    real_account_future: '',
    real_prod: '01',
    // AI 자동거래 금지종목 (쉼표 구분)
    blocked_symbols: '',
    blocked_buy_symbols: '',
    blocked_sell_symbols: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    if (user) {
      const trading = user.trading || {};
      setFormData({
        nickname: user.nickname || '',
        name: user.name || '',
        email: user.email || '',
        // 모의투자
        paper_app_key: trading.paper_app_key || trading.key_value_1 || '',
        paper_app_secret: trading.paper_app_secret || trading.key_value_2 || '',
        paper_account_no: trading.paper_account_no || trading.account_no || '',
        paper_hts_id: trading.paper_hts_id || '',
        paper_account_stock: trading.paper_account_stock || (trading.paper_account_no ? trading.paper_account_no.substring(0, 8) : '') || '',
        paper_account_future: trading.paper_account_future || '',
        paper_prod: trading.paper_prod || '01',
        // 실전투자
        real_app_key: trading.real_app_key || '',
        real_app_secret: trading.real_app_secret || '',
        real_account_no: trading.real_account_no || '',
        real_hts_id: trading.real_hts_id || '',
        real_account_stock: trading.real_account_stock || (trading.real_account_no ? trading.real_account_no.substring(0, 8) : '') || '',
        real_account_future: trading.real_account_future || '',
        real_prod: trading.real_prod || '01',
        blocked_symbols: Array.isArray(trading.blocked_symbols) ? trading.blocked_symbols.join(', ') : '',
        blocked_buy_symbols: Array.isArray(trading.blocked_buy_symbols) ? trading.blocked_buy_symbols.join(', ') : '',
        blocked_sell_symbols: Array.isArray(trading.blocked_sell_symbols) ? trading.blocked_sell_symbols.join(', ') : ''
      });
      setIsPaperTrading(trading.is_paper_trading !== false); // 기본값: 모의투자
    }
  }, [user]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
    setError('');
    setSuccess('');
  };

  const handleSwitchChange = (e) => {
    const checked = e.target.checked;
    setIsPaperTrading(checked);
    setError('');
    setSuccess('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      // 실명 필수 체크
      if (!formData.name || formData.name.trim() === '') {
        setError('주식거래 서비스 이용을 위해 실명(이름)이 필수입니다.');
        setLoading(false);
        return;
      }

      // HTS ID 필수 체크
      const currentHtsId = isPaperTrading ? formData.paper_hts_id : formData.real_hts_id;
      if (!currentHtsId || currentHtsId.trim() === '') {
        setError(`${isPaperTrading ? '모의투자' : '실전투자'} HTS ID는 필수입니다.`);
        setLoading(false);
        return;
      }

      // 실전 모드 전환 시 KIS 필수 필드 검증
      if (!isPaperTrading) {
        const missing = [];
        if (!formData.real_app_key?.trim()) missing.push('실전 App Key');
        if (!formData.real_app_secret?.trim()) missing.push('실전 App Secret');
        const realAccount =
          (formData.real_account_stock && formData.real_prod
            ? `${formData.real_account_stock}${formData.real_prod}`
            : formData.real_account_no) || '';
        if (!realAccount.trim()) missing.push('실전 계좌번호');
        if (!formData.real_hts_id?.trim()) missing.push('실전 HTS ID');
        if (missing.length > 0) {
          setError(`실전투자 전환 시 필수: ${missing.join(', ')}`);
          setLoading(false);
          return;
        }
      }

      // 현재 선택된 모드에 따라 API 키 설정
      const currentAppKey = isPaperTrading ? formData.paper_app_key : formData.real_app_key;
      const currentAppSecret = isPaperTrading ? formData.paper_app_secret : formData.real_app_secret;
      // 계좌번호 자동 계산 (앞 8자리 + 뒤 2자리)
      const currentAccountNo = isPaperTrading 
        ? (formData.paper_account_stock && formData.paper_prod ? `${formData.paper_account_stock}${formData.paper_prod}` : formData.paper_account_no)
        : (formData.real_account_stock && formData.real_prod ? `${formData.real_account_stock}${formData.real_prod}` : formData.real_account_no);
      const currentAccountStock = isPaperTrading ? formData.paper_account_stock : formData.real_account_stock;
      const currentAccountFuture = isPaperTrading ? formData.paper_account_future : formData.real_account_future;
      const currentProd = isPaperTrading ? formData.paper_prod : formData.real_prod;

      // 요청 데이터 로깅
      // name이 빈 문자열이 아닌 경우에만 전송 (빈 문자열도 전송하여 명시적으로 처리)
      const requestData = {
        nickname: formData.nickname,
        name: formData.name || '', // 빈 문자열로 전송 (undefined 방지, 주식거래 서비스 사용 시 필수)
        trading: {
          // 현재 사용 중인 키 (스위치에 따라)
          key_value_1: currentAppKey,
          key_value_2: currentAppSecret,
          account_no: currentAccountNo,
          hts_id: currentHtsId,
          account_stock: currentAccountStock,
          account_future: currentAccountFuture,
          account_prod: currentProd,
          is_paper_trading: isPaperTrading,
          // 모의투자 설정 (별도 저장)
          paper_app_key: formData.paper_app_key,
          paper_app_secret: formData.paper_app_secret,
          paper_account_no: formData.paper_account_stock && formData.paper_prod ? `${formData.paper_account_stock}${formData.paper_prod}` : formData.paper_account_no,
          paper_hts_id: formData.paper_hts_id,
          paper_account_stock: formData.paper_account_stock,
          paper_account_future: formData.paper_account_future,
          paper_prod: formData.paper_prod,
          // 실전투자 설정 (별도 저장)
          real_app_key: formData.real_app_key,
          real_app_secret: formData.real_app_secret,
          real_account_no: formData.real_account_stock && formData.real_prod ? `${formData.real_account_stock}${formData.real_prod}` : formData.real_account_no,
          real_hts_id: formData.real_hts_id,
          real_account_stock: formData.real_account_stock,
          real_account_future: formData.real_account_future,
          real_prod: formData.real_prod,
          blocked_symbols: formData.blocked_symbols.split(',').map(s => s.trim()).filter(Boolean),
          blocked_buy_symbols: formData.blocked_buy_symbols.split(',').map(s => s.trim()).filter(Boolean),
          blocked_sell_symbols: formData.blocked_sell_symbols.split(',').map(s => s.trim()).filter(Boolean)
        }
      };
      
      console.log('Profile update request data:', {
        nickname: requestData.nickname,
        name: requestData.name,
        nameType: typeof requestData.name,
        nameLength: requestData.name ? requestData.name.length : 0,
        nameIsEmpty: requestData.name === '',
        hasTrading: !!requestData.trading
      });

      const response = await authenticatedFetch('/api/user/settings', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestData),
      });

      if (response.ok) {
        const data = await response.json();
        console.log('Profile update response:', {
          nickname: data.user?.nickname,
          name: data.user?.name,
          hasTrading: !!data.user?.trading
        });
        setSuccess('프로필이 성공적으로 업데이트되었습니다.');
        if (onUpdate) {
          // 업데이트된 사용자 정보 전달 (실명 포함)
          onUpdate({
            id: data.user?.id,
            email: data.user?.email,
            nickname: data.user?.nickname,
            name: data.user?.name,
            services: data.user?.services,
            trading: {
              ...data.user.trading,
              is_paper_trading: isPaperTrading,
              blocked_symbols: data.user.trading?.blocked_symbols ?? [],
              blocked_buy_symbols: data.user.trading?.blocked_buy_symbols ?? [],
              blocked_sell_symbols: data.user.trading?.blocked_sell_symbols ?? []
            }
          });
        }
        setTimeout(() => {
          if (onClose) onClose();
        }, 1500);
      } else {
        const errorData = await response.json();
        setError(errorData.error || '프로필 업데이트 실패');
      }
    } catch (err) {
      setError('서버 오류가 발생했습니다.');
      console.error('Profile update error:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="profile-overlay" onClick={onClose}>
      <div className="profile-modal" onClick={(e) => e.stopPropagation()}>
        <div className="profile-header">
          <h2>프로필 설정</h2>
          <div className="profile-header-actions">
            <button
              type="button"
              className="profile-goto-trade"
              onClick={() => {
                if (onClose) onClose();
                if (typeof window.setTradingTab === 'function') {
                  window.setTradingTab('trade');
                }
              }}
            >
              거래하기
            </button>
            <button className="profile-close" onClick={onClose}>×</button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="profile-form">
          {error && (
            <div className="profile-error">
              {error}
            </div>
          )}

          {success && (
            <div className="profile-success">
              {success}
            </div>
          )}

          <div className="form-group">
            <label>닉네임 (필수)</label>
            <input
              type="text"
              name="nickname"
              value={formData.nickname}
              onChange={handleChange}
              placeholder="닉네임을 입력하세요"
              maxLength="50"
              required
            />
            <small>서비스에서 표시될 별명입니다</small>
          </div>

          <div className="form-group">
            <label>이름 (실명, 필수)</label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="실명을 입력하세요 (필수)"
              maxLength="50"
              required
            />
            <small>주식거래 서비스 이용을 위해 실명이 필수입니다</small>
          </div>

          <div className="form-group">
            <label>이메일</label>
            <input
              type="email"
              name="email"
              value={formData.email}
              disabled
              className="disabled-input"
            />
            <small>이메일은 변경할 수 없습니다.</small>
          </div>

          {/* 모의/실전 전환 스위치 */}
          <div className="form-divider">
            <div className="trading-mode-switch">
              <label className="switch-label">
                <span className={!isPaperTrading ? 'active' : ''}>실전투자</span>
                <label className="switch">
                  <input
                    type="checkbox"
                    checked={isPaperTrading}
                    onChange={handleSwitchChange}
                  />
                  <span className="slider"></span>
                </label>
                <span className={isPaperTrading ? 'active' : ''}>모의투자</span>
              </label>
              <p className="switch-hint">
                현재 모드: <strong>{isPaperTrading ? '모의투자' : '실전투자'}</strong>
              </p>
            </div>
          </div>

          {/* 탭 메뉴 */}
          <div className="profile-tabs">
            <button
              type="button"
              className={activeTab === 'paper' ? 'active' : ''}
              onClick={() => setActiveTab('paper')}
            >
              📝 모의투자 설정
            </button>
            <button
              type="button"
              className={activeTab === 'real' ? 'active' : ''}
              onClick={() => setActiveTab('real')}
            >
              💰 실전투자 설정
            </button>
          </div>

          {/* 모의투자 탭 */}
          {activeTab === 'paper' && (
            <div className="tab-content">
              <div className="form-divider">
                <h3>모의투자 API 설정</h3>
                <p className="form-hint">한투 Open API 모의투자 서비스 신청 후 발급받은 키를 입력하세요</p>
              </div>

              <div className="form-group">
                <label>모의투자 API Key (App Key)</label>
                <input
                  type="text"
                  name="paper_app_key"
                  value={formData.paper_app_key}
                  onChange={handleChange}
                  placeholder="모의투자 Open API App Key"
                />
              </div>

              <div className="form-group">
                <label>모의투자 API Secret (App Secret)</label>
                <input
                  type="password"
                  name="paper_app_secret"
                  value={formData.paper_app_secret}
                  onChange={handleChange}
                  placeholder="모의투자 Open API App Secret"
                />
              </div>

              <div className="form-group">
                <label>모의투자 HTS ID (필수)</label>
                <input
                  type="text"
                  name="paper_hts_id"
                  value={formData.paper_hts_id}
                  onChange={handleChange}
                  placeholder="모의투자 HTS ID (KIS Developers 고객 ID)"
                  required
                />
                <small>체결통보, 나의 조건 목록 확인 등에 사용됩니다 (필수 입력)</small>
              </div>

              <div className="form-group">
                <label>모의투자 증권계좌 앞 8자리</label>
                <input
                  type="text"
                  name="paper_account_stock"
                  value={formData.paper_account_stock}
                  onChange={handleChange}
                  placeholder="모의투자 증권계좌 앞 8자리 (예: 12345678)"
                  maxLength="8"
                />
              </div>

              <div className="form-group">
                <label>모의투자 선물옵션계좌 앞 8자리 (선택)</label>
                <input
                  type="text"
                  name="paper_account_future"
                  value={formData.paper_account_future}
                  onChange={handleChange}
                  placeholder="모의투자 선물옵션계좌 앞 8자리 (선택사항)"
                  maxLength="8"
                />
              </div>

              <div className="form-group">
                <label>모의투자 계좌번호 뒤 2자리</label>
                <select
                  name="paper_prod"
                  value={formData.paper_prod}
                  onChange={handleChange}
                >
                  <option value="01">01 - 종합계좌</option>
                  <option value="03">03 - 국내선물옵션 계좌</option>
                  <option value="08">08 - 해외선물옵션 계좌</option>
                  <option value="22">22 - 연금저축 계좌</option>
                  <option value="29">29 - 퇴직연금 계좌</option>
                </select>
                <small>기본값: 01 (종합계좌)</small>
              </div>

              <div className="form-group">
                <label>모의투자 계좌번호 (자동 계산)</label>
                <input
                  type="text"
                  value={formData.paper_account_stock && formData.paper_prod ? `${formData.paper_account_stock}${formData.paper_prod}` : ''}
                  disabled
                  className="disabled-input"
                  placeholder="증권계좌 앞 8자리 + 뒤 2자리"
                />
                <small>위의 증권계좌와 계좌번호 뒤 2자리를 입력하면 자동으로 계산됩니다</small>
              </div>
            </div>
          )}

          {/* 실전투자 탭 */}
          {activeTab === 'real' && (
            <div className="tab-content">
              <div className="form-divider">
                <h3>실전투자 API 설정</h3>
                <p className="form-hint">한투 Open API 실전투자 서비스 신청 후 발급받은 키를 입력하세요</p>
              </div>

              <div className="form-group">
                <label>실전투자 API Key (App Key)</label>
                <input
                  type="text"
                  name="real_app_key"
                  value={formData.real_app_key}
                  onChange={handleChange}
                  placeholder="실전투자 Open API App Key"
                />
              </div>

              <div className="form-group">
                <label>실전투자 API Secret (App Secret)</label>
                <input
                  type="password"
                  name="real_app_secret"
                  value={formData.real_app_secret}
                  onChange={handleChange}
                  placeholder="실전투자 Open API App Secret"
                />
              </div>

              <div className="form-group">
                <label>실전투자 HTS ID (필수)</label>
                <input
                  type="text"
                  name="real_hts_id"
                  value={formData.real_hts_id}
                  onChange={handleChange}
                  placeholder="실전투자 HTS ID (KIS Developers 고객 ID)"
                  required
                />
                <small>체결통보, 나의 조건 목록 확인 등에 사용됩니다 (필수 입력)</small>
              </div>

              <div className="form-group">
                <label>실전투자 증권계좌 앞 8자리</label>
                <input
                  type="text"
                  name="real_account_stock"
                  value={formData.real_account_stock}
                  onChange={handleChange}
                  placeholder="실전투자 증권계좌 앞 8자리 (예: 12345678)"
                  maxLength="8"
                />
              </div>

              <div className="form-group">
                <label>실전투자 선물옵션계좌 앞 8자리 (선택)</label>
                <input
                  type="text"
                  name="real_account_future"
                  value={formData.real_account_future}
                  onChange={handleChange}
                  placeholder="실전투자 선물옵션계좌 앞 8자리 (선택사항)"
                  maxLength="8"
                />
              </div>

              <div className="form-group">
                <label>실전투자 계좌번호 뒤 2자리</label>
                <select
                  name="real_prod"
                  value={formData.real_prod}
                  onChange={handleChange}
                >
                  <option value="01">01 - 종합계좌</option>
                  <option value="03">03 - 국내선물옵션 계좌</option>
                  <option value="08">08 - 해외선물옵션 계좌</option>
                  <option value="22">22 - 연금저축 계좌</option>
                  <option value="29">29 - 퇴직연금 계좌</option>
                </select>
                <small>기본값: 01 (종합계좌)</small>
              </div>

              <div className="form-group">
                <label>실전투자 계좌번호 (자동 계산)</label>
                <input
                  type="text"
                  value={formData.real_account_stock && formData.real_prod ? `${formData.real_account_stock}${formData.real_prod}` : ''}
                  disabled
                  className="disabled-input"
                  placeholder="증권계좌 앞 8자리 + 뒤 2자리"
                />
                <small>위의 증권계좌와 계좌번호 뒤 2자리를 입력하면 자동으로 계산됩니다</small>
              </div>
            </div>
          )}

          {/* AI 자동거래 금지종목 */}
          <div className="form-divider">
            <h3>AI 자동거래 금지종목</h3>
            <p className="form-hint">종목코드를 쉼표로 구분하여 입력하세요. AI 자동거래 시 해당 종목은 매수/매도에서 제외됩니다.</p>
          </div>
          <div className="form-group">
            <label>거래 금지 종목 (매수·매도 모두 금지)</label>
            <input
              type="text"
              name="blocked_symbols"
              value={formData.blocked_symbols}
              onChange={handleChange}
              placeholder="예: 005930, 000660, 035720"
            />
          </div>
          <div className="form-group">
            <label>매수 금지 종목 (매수만 금지)</label>
            <input
              type="text"
              name="blocked_buy_symbols"
              value={formData.blocked_buy_symbols}
              onChange={handleChange}
              placeholder="예: 005930, 000660"
            />
          </div>
          <div className="form-group">
            <label>매도 금지 종목 (매도만 금지)</label>
            <input
              type="text"
              name="blocked_sell_symbols"
              value={formData.blocked_sell_symbols}
              onChange={handleChange}
              placeholder="예: 035720"
            />
          </div>

          <div className="profile-actions">
            <button
              type="button"
              className="btn-cancel"
              onClick={onClose}
            >
              취소
            </button>
            <button
              type="submit"
              className="btn-save"
              disabled={loading}
            >
              {loading ? '저장 중...' : '저장'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Profile;
